/**
 * Global configuration utility for environment variables
 */

const config = {
  // API Configuration
  // May contain the literal `CLIENT` placeholder (e.g. https://CLIENT.reppi.co/api),
  // which is swapped for the company the user logged in with.
  API_BASE_URL: process.env.REACT_APP_API_BASE_URL,

  // Add other environment variables here as needed
  // EXAMPLE: API_TIMEOUT: process.env.REACT_APP_API_TIMEOUT || 5000,
  // EXAMPLE: ENVIRONMENT: process.env.NODE_ENV || 'development',
};

const COMPANY_STORAGE_KEY = "reppi_company";
const COMPANY_PLACEHOLDER = /CLIENT/g;
// Used when no company is known yet (logged out, or a call made before login)
export const DEFAULT_COMPANY = "global";

// The company is needed by every API call, including the ones that run before
// React mounts (AuthContext's token refresh), so it lives in localStorage and is
// mirrored here for synchronous access.
const readStoredCompany = () => {
  try {
    return localStorage.getItem(COMPANY_STORAGE_KEY) || "";
  } catch (error) {
    return "";
  }
};

let currentCompany = readStoredCompany();

export const normalizeCompany = company =>
  typeof company === "string" ? company.trim().toLowerCase() : "";

// Returns the company currently used to build API URLs
export const getApiCompany = () => currentCompany;

// Persists the company from the login form / JWT so every later call uses it
export const setApiCompany = company => {
  const normalized = normalizeCompany(company);
  if (!normalized) return currentCompany;

  currentCompany = normalized;
  try {
    localStorage.setItem(COMPANY_STORAGE_KEY, normalized);
  } catch (error) {
    // Storage unavailable (private mode) - in-memory value still works
  }
  return currentCompany;
};

export const clearApiCompany = () => {
  currentCompany = "";
  try {
    localStorage.removeItem(COMPANY_STORAGE_KEY);
  } catch (error) {
    // Nothing to clean up
  }
};

// Swaps the CLIENT placeholder in the base URL for the given company, falling
// back to DEFAULT_COMPANY when none is known so the placeholder never leaks into
// a request. Base URLs without the placeholder are returned untouched.
export const buildApiBaseUrl = company => {
  const baseUrl = config.API_BASE_URL || "";
  const normalized = normalizeCompany(company) || DEFAULT_COMPANY;
  return baseUrl.replace(COMPANY_PLACEHOLDER, normalized);
};

// Helper function to get API base URL
export const getApiBaseUrl = () => {
  return buildApiBaseUrl(currentCompany);
};

// Helper function to get full API endpoint
export const getApiEndpoint = path => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${getApiBaseUrl()}${cleanPath}`;
};

// For public pages that learn their company from the URL rather than a login
// (e.g. the customer welcome survey). Builds against that company without
// touching the stored one, so an admin's own session isn't repointed.
export const getCompanyApiEndpoint = (company, path) => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${buildApiBaseUrl(company)}${cleanPath}`;
};

// For endpoints that are never company-scoped (login, token) - they run against
// the global host because the company isn't known until the JWT comes back.
export const getGlobalApiEndpoint = path => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${buildApiBaseUrl(DEFAULT_COMPANY)}${cleanPath}`;
};

// Reppi admin endpoints (/v1/admin/*) live on their own host - never the logged
// in company's host or the global one. The company they act on is carried by the
// X-Company-Code header instead of the subdomain.
export const ADMIN_COMPANY = "admin";

export const getAdminApiEndpoint = path => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${buildApiBaseUrl(ADMIN_COMPANY)}${cleanPath}`;
};

// The company a Reppi admin is currently working on. Admin pages carry the code
// in their URL, so this is only the fallback for the admin tools that aren't
// opened from a company page (rep token, Agora backfill).
const ADMIN_COMPANY_CODE_KEY = "reppi_admin_company_code";

const readStoredAdminCompanyCode = () => {
  try {
    return localStorage.getItem(ADMIN_COMPANY_CODE_KEY) || "";
  } catch (error) {
    return "";
  }
};

let currentAdminCompanyCode = readStoredAdminCompanyCode();

export const getAdminCompanyCode = () => currentAdminCompanyCode;

export const setAdminCompanyCode = code => {
  const normalized = typeof code === "string" ? code.trim() : "";
  if (!normalized || normalized === currentAdminCompanyCode) {
    return currentAdminCompanyCode;
  }

  currentAdminCompanyCode = normalized;
  try {
    localStorage.setItem(ADMIN_COMPANY_CODE_KEY, normalized);
  } catch (error) {
    // Storage unavailable (private mode) - in-memory value still works
  }
  return currentAdminCompanyCode;
};

export const clearAdminCompanyCode = () => {
  currentAdminCompanyCode = "";
  try {
    localStorage.removeItem(ADMIN_COMPANY_CODE_KEY);
  } catch (error) {
    // Nothing to clean up
  }
};

// Export the full config object
export default config;
